import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, ShieldCheck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      return;
    }
    const fetchInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.post(
          `${API}/payments/create-payment-link`,
          { booking_id: bookingId },
          config
        );
        setInfo(res.data);
      } catch (error) {
        toast.error('Failed to load booking');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading booking details…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Sparkles className="w-16 h-16 text-purple-300 mx-auto mb-4" />
            <h1
              className="text-4xl font-bold text-white mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Complete Your Payment
            </h1>
            <p className="text-purple-200">UPI for India · PayPal for international clients</p>
          </div>

          <Card className="bg-gradient-to-br from-purple-800/50 to-indigo-900/50 backdrop-blur-lg border border-white/30 p-8 text-center mb-8 spiritual-glow">
            <p className="text-purple-200 text-lg mb-2">Total Amount</p>
            <div className="text-6xl font-bold text-white mb-2">₹{info?.amount ?? '—'}</div>
            {bookingId && (
              <p className="text-purple-300 text-sm">Booking ID: {bookingId}</p>
            )}
          </Card>

          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <ShieldCheck className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
              <div className="text-purple-100">
                <h4 className="font-bold text-white mb-1">Secure Checkout</h4>
                <p className="text-sm">
                  Pay instantly via UPI (GPay, PhonePe, BHIM) if you&apos;re in India, or
                  via PayPal&apos;s buyer-protected checkout for international payments.
                  After paying, confirm on WhatsApp to receive your report.
                </p>
              </div>
            </div>
          </Card>

          <div className="flex flex-col space-y-4">
            <Button
              onClick={() => navigate('/pay')}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-6 text-lg font-semibold"
              data-testid="continue-to-cashfree-btn"
            >
              Continue to Secure Checkout
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full border-purple-500/30 text-purple-200 hover:bg-purple-900/40 py-6"
              data-testid="back-dashboard-btn"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
